import { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import "./index.css";

import pytorchLogo from "../assets/logos/pytorch.svg";
import tensorflowLogo from "../assets/logos/tensorflow.svg";
import jupyterLogo from "../assets/logos/jupyter.svg";
import nvidiaLogo from "../assets/logos/nvidia.svg";
import dockerLogo from "../assets/logos/docker.svg";
import matlabLogo from "../assets/logos/matlab.png";
import onnxLogo from "../assets/logos/onnx.svg";
import kubernetesLogo from "../assets/logos/kubernetes.svg";
import anacondaLogo from "../assets/logos/anaconda.svg";

const FRAMEWORKS = [
  { name: "PyTorch", logo: pytorchLogo },
  { name: "TensorFlow", logo: tensorflowLogo },
  { name: "Jupyter", logo: jupyterLogo },
  { name: "CUDA", logo: nvidiaLogo },
  { name: "cuDNN", logo: nvidiaLogo },
  { name: "Docker", logo: dockerLogo },
  { name: "MATLAB", logo: matlabLogo },
  { name: "ONNX", logo: onnxLogo },
  { name: "Kubernetes", logo: kubernetesLogo },
  { name: "Anaconda", logo: anacondaLogo }
];

export default function App() {
  const rootRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const inView = useInView(rootRef, { margin: "-10% 0px", once: true });
  const shouldAnimate = inView && !prefersReducedMotion;

  const fade = (delay = 0) => ({
    initial: { opacity: 0, y: 18 },
    animate: shouldAnimate ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 },
    transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }
  });

  return (
    <section ref={rootRef} className="fw">
      <div className="fw-inner">
        <div className="fw-header">
          <motion.span className="fw-eyebrow" {...fade(0)}>
            <span className="fw-eyebrow-mark" />
            AI Framework Compatibility
          </motion.span>

          <motion.h2 className="fw-title" {...fade(0.1)}>
            Engineered for{" "}
            <span className="fw-title-accent">PyTorch, TensorFlow, and CUDA</span>
          </motion.h2>
        </div>

        <div className="fw-body-wrap">
          <motion.p className="fw-body fw-body-lead" {...fade(0.2)}>
            ArsenalPC AI workstations are built for PyTorch, TensorFlow, CUDA,
            cuDNN, and JAX workflows, with first-class support for Jupyter,
            Hugging Face Transformers, ONNX, and Docker.
          </motion.p>

          <motion.p className="fw-body" {...fade(0.3)}>
            We tune hardware for stable framework support and provide setup
            guidance so your team can get running quickly with Anaconda
            environments, MATLAB, or Kubernetes-orchestrated training pipelines.
          </motion.p>

          <motion.a
            className="fw-cta"
            href="https://arsenalpc.com/shop/ai-workstations/"
            {...fade(0.4)}
          >
            Shop Workstations
            <span className="fw-cta-arrow" aria-hidden="true">→</span>
          </motion.a>
        </div>

        <motion.div
          className="fw-visual"
          initial={{ opacity: 0, y: 24 }}
          animate={shouldAnimate ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="fw-stack">
            {FRAMEWORKS.map((f, i) => (
              <motion.div
                key={f.name}
                className="fw-item"
                initial={{ opacity: 0, y: 14 }}
                animate={
                  shouldAnimate
                    ? { opacity: 1, y: 0 }
                    : { opacity: 1, y: 0 }
                }
                transition={{
                  duration: 0.5,
                  delay: 0.45 + i * 0.07,
                  ease: [0.22, 1, 0.36, 1]
                }}
              >
                <div className="fw-item-logo">
                  <img src={f.logo} alt={`${f.name} logo`} />
                </div>
                <span className="fw-item-name">{f.name}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
